// ============================================================================
// ToneSwatches — the six review-label colours, as a real radio group.
// ----------------------------------------------------------------------------
// Lifted out of ReviewLabelsSection (migration 088 wave) when the Run Sheet
// picker gained inline label creation. Two surfaces now let an operator choose a
// tone; a copy in each is a palette that drifts, and the migration's CHECK
// constraint plus the CSS mirror in dashboard-runsheet.css are already two other
// places the six slugs are written down. Three is the ceiling.
//
// WHY THE SELECTED STATE IS NOT COLOUR. Six coloured squares with one of them
// slightly different is not a state anyone can read, and it would fail WCAG
// 1.4.1 outright. The chosen swatch carries a heavier border IN ITS OWN COLOUR
// *and* a tick glyph, so the selection survives both a monochrome render and a
// colour-blind reader. `aria-checked` carries it for assistive tech, and
// `aria-label` is the tone's NAME rather than its hex.
//
// KNOWN DEBT, deliberately inherited rather than fixed here: each radio is its
// own tab stop, where the ARIA pattern wants one tab stop plus arrow-key roving.
// Changing it would change the Settings surface's keyboard behaviour in the same
// commit that adds a Run Sheet feature, so it is left alone. It is tolerable
// because a tone is always pre-selected, so the group can be skipped entirely.
// ============================================================================

import React from 'react';
import { Icons } from './Icon';
import { TONES, TONE_ORDER, resolveTone, type ReviewLabelTone } from '../../services/review-labels';

export interface ToneSwatchesProps {
    value: ReviewLabelTone;
    onChange: (t: ReviewLabelTone) => void;
    /** Namespaces the per-swatch DOM ids. Two pickers can be mounted at once. */
    idPrefix: string;
    /** Edge length in px. Settings uses the 22px default; the Run Sheet popover
     *  is a 240px panel and passes 20 so six swatches plus gaps still fit. */
    size?: number;
    disabled?: boolean;
}

export default function ToneSwatches({
    value, onChange, idPrefix, size = 22, disabled = false,
}: ToneSwatchesProps) {
    const selected = resolveTone(value);
    return (
        <div role="radiogroup" aria-label="Label colour" style={{ display: 'flex', gap: '4px' }}>
            {TONE_ORDER.map((t) => {
                const tone = TONES[t];
                const on = t === selected;
                return (
                    <button
                        key={t}
                        type="button"
                        id={`${idPrefix}-tone-${t}`}
                        role="radio"
                        aria-checked={on}
                        aria-label={tone.label}
                        title={tone.label}
                        disabled={disabled}
                        // qm-inline-action-btn or the global button theme floors
                        // this at 44px and the row becomes a toolbar.
                        className="qm-inline-action-btn"
                        onClick={() => onChange(t)}
                        style={{
                            width: `${size}px`,
                            height: `${size}px`,
                            minHeight: 0,
                            padding: 0,
                            borderRadius: '6px',
                            background: tone.tint,
                            border: on ? `2px solid ${tone.fg}` : `1.5px solid ${tone.edge}`,
                            color: tone.fg,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: disabled ? 'default' : 'pointer',
                        }}
                    >
                        {on ? <Icons.Check size={Math.max(10, size - 10)} aria-hidden="true" /> : null}
                    </button>
                );
            })}
        </div>
    );
}

/**
 * The tone a NEW label should arrive pre-armed with: the first slot in
 * TONE_ORDER that no existing label already uses, falling back to the neutral
 * slot once all six are taken.
 *
 * Without this every label an operator creates in a hurry is slate, and a
 * six-colour palette that renders as six identical grey chips is decoration.
 * Computed ONCE when a create block opens and never recomputed while it is
 * open: re-deriving it per keystroke silently overwrites an explicit pick the
 * moment the operator backspaces the name to empty.
 */
export function nextUnusedTone(labels: Array<{ tone?: unknown }>): ReviewLabelTone {
    const used = new Set(labels.map(l => resolveTone(l.tone)));
    return TONE_ORDER.find(t => !used.has(t)) ?? 'slate';
}
